/**
 * Which sector each holding belongs to, where the issuer does not say.
 *
 * NOT PUBLISHED DATA. Everything in `shared/data` comes from the fund providers
 * and reads N/A when they are silent; this file is the exception and is the
 * app's own classification, which is why it lives here and not there. Hamilton
 * publishes that HMAX is 75.9% Banks and separately that it holds RY, TD and the
 * rest — never which holding is in which slice. This joins the two.
 *
 * Keyed by fund, not by ticker, because the taxonomy is the fund's own and they
 * disagree: PLD is "Real Estate" inside SMAX and "Industrial REITs" inside RMAX,
 * and both are that issuer's word for the same company. A global ticker→sector
 * map would have to pick one and be wrong on the other page.
 *
 * The labels must match the published slice names exactly — they are what a
 * click is matched against. A typo here does not fail loudly; it produces a
 * sector with nothing in it, which is what `bun run check:sectors` is for: it
 * adds up the weights of the holdings mapped to each slice and compares them
 * against the weight the issuer published for it.
 *
 * Funds of funds are absent on purpose, and now resolve anyway. HDIV and HYLD
 * hold other ETFs, so no holding of theirs is a company and there is nothing
 * here to key on. Their slices are answered by walking down into the funds they
 * hold and using the classification of whichever fund holds the company
 * directly — see src/data/look-through.ts. Adding HYLD here would mean writing
 * the same company twice under two issuers' names.
 *
 * Which is also why the labels below need not agree between funds: a slice
 * published as "Information Technology" by one fund and "Technology" by another
 * is matched through the roll-up in src/data/sector-canon.ts rather than by
 * forcing every fund onto one vocabulary that none of them publishes.
 */
export const HOLDING_SECTORS: Record<string, Record<string, string>> = {
    // Banks / Insurance / Asset Management.
    HMAX: {
        RY: 'Banks',
        TD: 'Banks',
        BMO: 'Banks',
        CM: 'Banks',
        BNS: 'Banks',
        NA: 'Banks',
        MFC: 'Insurance',
        GWO: 'Insurance',
        SLF: 'Insurance',
        // Brookfield Corp is the asset manager, not a bank or an insurer.
        BN: 'Asset Management',
    },

    // Utilities / Industrials / Pipelines / Communication Services.
    UMAX: {
        H: 'Utilities',
        FTS: 'Utilities',
        EMA: 'Utilities',
        // Brookfield Infrastructure sits in the utilities slice here, which is
        // what makes that slice reconcile — 31.3% needs all four.
        'BIP-U': 'Utilities',
        CNR: 'Industrials',
        CP: 'Industrials',
        WCN: 'Industrials',
        TRP: 'Pipelines',
        ENB: 'Pipelines',
        PPL: 'Pipelines',
        BCE: 'Communication Services',
        'RCI-B': 'Communication Services',
        T: 'Communication Services',
    },

    // The GICS-shaped eleven.
    SMAX: {
        AMD: 'Technology',
        MU: 'Technology',
        CSCO: 'Technology',
        AAPL: 'Technology',
        MSFT: 'Technology',
        NVDA: 'Technology',
        AVGO: 'Technology',
        PLTR: 'Technology',
        // Visa is Financials, not Technology — GICS moved the payment networks
        // in 2023, and the published 12.3% only reconciles with it here.
        V: 'Financials',
        JPM: 'Financials',
        'BRK-B': 'Financials',
        GOOGL: 'Communications',
        META: 'Communications',
        NFLX: 'Communications',
        LLY: 'Healthcare',
        JNJ: 'Healthcare',
        GE: 'Industrials',
        CAT: 'Industrials',
        AMZN: 'Consumer Discretionary',
        TSLA: 'Consumer Discretionary',
        PLD: 'Real Estate',
        LIN: 'Materials',
        XOM: 'Energy',
        NEE: 'Utilities',
        WMT: 'Consumer Staples',
    },

    // The four below publish no sector breakdown at all, so unlike the ones
    // above there are no weights to reconcile against — `check:sectors` can
    // confirm the labels are used consistently but cannot confirm they are
    // right. That is why these stay at the coarsest honest grain: every holding
    // gets the sector nobody would argue about, and none of them gets a
    // sub-sector I would be inventing. Splitting EMAX into integrated, E&P and
    // refining is a judgement call on names like Cenovus and Canadian Natural
    // that reasonable people classify differently, and a wrong split here would
    // be indistinguishable from a published one.
    //
    // EMAX is an energy fund holding energy companies. The single bucket is the
    // whole truth about it.
    EMAX: Object.fromEntries(
        ['MPC', 'VLO', 'PSX', 'CVE', 'FANG', 'EOG', 'SU', 'CNQ', 'IMO', 'BKR', 'COP', 'CVX', 'OXY', 'XOM', 'TPL', 'SLB', 'DVN', 'EXE', 'EQT', 'HAL'].map((t) => [t, 'Energy']),
    ),

    // Not one bucket, and that is the point: a technology fund holding Amazon,
    // Tesla, Alphabet, Meta and Netflix is a third not-technology by GICS, which
    // is worth seeing rather than smoothing over.
    QMAX: {
        AMD: 'Technology',
        AMAT: 'Technology',
        MSFT: 'Technology',
        MU: 'Technology',
        AAPL: 'Technology',
        LRCX: 'Technology',
        INTC: 'Technology',
        AVGO: 'Technology',
        NVDA: 'Technology',
        PLTR: 'Technology',
        GOOGL: 'Communication Services',
        META: 'Communication Services',
        NFLX: 'Communication Services',
        AMZN: 'Consumer Discretionary',
        TSLA: 'Consumer Discretionary',
    },

    // Gold rather than Materials, which is the GICS parent: HDIV publishes "Gold"
    // as a sector of its own, so this matches a label the data already contains.
    AMAX: Object.fromEntries(
        ['HMY', 'AEM', 'FNV', 'CDE', 'RGLD', 'AU', 'WPM', 'B', 'NEM', 'GFI', 'EDV', 'KGC', 'PAAS', 'HL', 'AGI'].map((t) => [t, 'Gold']),
    ),

    LMAX: Object.fromEntries(
        ['ABBV', 'UNH', 'CVS', 'LLY', 'TMO', 'BMY', 'AMGN', 'JNJ', 'MRK', 'ABT', 'VRTX', 'CI', 'DHR', 'MDT', 'SYK', 'MCK', 'GILD', 'PFE', 'BSX', 'ISRG'].map((t) => [t, 'Healthcare']),
    ),

    // REIT sub-sectors, which are the least clean of these: several of these
    // trusts are genuinely diversified and the published slices do not name an
    // office bucket for the one that is mostly office.
    RMAX: {
        SPG: 'Retail REITs',
        O: 'Retail REITs',
        'REI-U': 'Retail REITs',
        'SRU-U': 'Retail REITs',
        'FCR-U': 'Retail REITs',
        'CHP-U': 'Retail REITs',
        'DIR-U': 'Industrial REITs',
        PLD: 'Industrial REITs',
        'GRT-U': 'Industrial REITs',
        WELL: 'Healthcare REITs',
        VTR: 'Healthcare REITs',
        DLR: 'Data Center REITs',
        EQIX: 'Data Center REITs',
        'BEI-U': 'Multi-Family Residential REITs',
        'CAR-U': 'Multi-Family Residential REITs',
        PSA: 'Self-Storage REITs',
        CBRE: 'Real Estate Services',
        'HR-U': 'Diversified REITs',
        AMT: 'Telecom Tower REITs',
        // AP-U (Allied Properties) is deliberately absent. It is an office REIT
        // and this fund publishes no office slice; putting it in Diversified
        // takes that slice to 9.7% against the 5.3% Hamilton states, so the
        // guess is provably wrong rather than merely unverified. It shows as
        // unclassified, which is the honest answer to a question the issuer
        // has not answered.
    },
};

/** The app's sector for one holding, or null where it has not been classified.
    Callers prefer the issuer's own `sector` field and fall back to this. */
export const classifiedSector = (fund: string, ticker: string | null): string | null => (ticker == null ? null : (HOLDING_SECTORS[fund]?.[ticker] ?? null));
